/*
 * Entra ID sign-in for the admin interface.
 *
 * The shape of this, and why
 * -------------------------
 * MSAL is configured from GET /api/config at runtime, not from build-time
 * environment variables, so the same bundle is deployable to every environment.
 * That makes the instance asynchronous to create, which is why there is a loading
 * gate before anything MSAL-dependent renders.
 *
 * Redirect, not popup. A popup is blocked often enough to be a support burden, and
 * conditional access policies - MFA prompts, compliant-device checks - behave far
 * better in a top-level navigation. handleRedirectPromise is awaited before the
 * app renders so the fragment MSAL leaves in the URL is consumed before React
 * Router sees it.
 *
 * Tokens live in sessionStorage rather than localStorage: closing the tab ends the
 * session, which is the right default for an administrative interface on a shared
 * machine. The cost is a silent re-authentication in a new tab, which is invisible
 * when the Entra ID session is still alive.
 *
 * Sign-in from ANY page (2026-10-09). The public header offers "Sign in", and the
 * reader comes back to the page they were on. Entra only knows one redirect URI,
 * /admin, so MSAL lands there, caches the response, and navigates to the page
 * that started the sign-in - which then has to run MSAL itself to finish it. A
 * public page normally never starts MSAL (anonymous visitors are not sent the
 * Entra identifiers), so a tab-scoped marker, SIGN_IN_HINT_KEY, tells it to: set
 * when a sign-in starts and while an account is held, cleared when MSAL finds
 * none or the reader signs out. The same marker is how a reload of a public page
 * still knows the reader is signed in. The access check, /api/admin/me, runs on
 * public pages too - the header shows "Manage" only to an account the server
 * confirms. Until 2026-10-15 it ran QUIETLY there - cached and refreshed tokens
 * only, never the redirect - so an expired session could not send a reader to
 * Entra while reading. The cost came the next day: a SPA's refresh token lives 24
 * hours, so the check got no token and the header lost "Manage" while still
 * offering "Sign out". By the owner's choice, a public page load that finds the
 * sign-in expired now renews it - through Entra and back to the same page
 * (acquireTokenOnPublicPage) - and so does moving to another page inside the
 * site (renewExpiredSignIn, called by useRenewOnNavigation).
 *
 * The public pages READ AS the signed-in reader too (2026-10-26, S46). Articles
 * can be private - read only by their audiences and by administrators - so the
 * content requests a public page makes carry the reader's token when there is
 * one: `reader` below, the same token the access check uses
 * (acquireTokenOnPublicPage, renewal included). A tab restoring a sign-in waits
 * for it before reading (`reader.ready`), so a private article never shows "not
 * found" first; an anonymous tab reads at once, as before.
 *
 * Group membership is NOT checked here. The browser decides nothing about
 * authorisation: it asks GET /api/admin/me, and the server - which verified the
 * token signature and the group claim - answers. A client-side group check would be
 * a UI convenience at best and a lie at worst, since anyone can edit what their own
 * browser believes.
 */

import {
  EventType,
  BrowserAuthError,
  CacheLookupPolicy,
  ClientAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AdminIdentity, ClientConfig, SignInConfig } from '@accotool-kb/shared';
import { ApiError } from '../api/client.js';
import { createAdminApi, type AdminApi } from '../api/adminApi.js';
import { getClientConfig, getSignInConfig } from '../api/publicApi.js';
import { ANONYMOUS_READER, type PublicReader } from '../api/publicReader.js';
import { noteSignOutReturn } from './signOutReturn.js';

export interface AuthState {
  /** Null until the runtime configuration has been fetched. */
  config: ClientConfig | null;
  /** Set when the configuration itself could not be loaded. */
  configError: string | null;
  ready: boolean;
  signedIn: boolean;
  /**
   * True while this tab holds a sign-in from earlier (SIGN_IN_HINT_KEY) that
   * MSAL has not finished restoring yet. The public header shows no account
   * action for it rather than flashing "Sign in" at someone who is signed in
   * (corrected 2026-10-15: this said "Manage", which it has never shown for it).
   */
  restoringSession: boolean;
  account: AccountInfo | null;
  /**
   * Server verdict on this account. Null until /me has answered - on a public
   * page also when the check could not get a token: a renewal that came back
   * still expired, or one that could not start (acquireTokenOnPublicPage).
   */
  identity: AdminIdentity | null;
  /** Set when the server refused: not in the group, or a misconfiguration. */
  authorisationError: string | null;
  checkingIdentity: boolean;
  /**
   * Start the Entra sign-in and come back to the current page. Works from any
   * page: where MSAL has not started yet - a public page - it is started first
   * and the redirect follows as soon as it is ready.
   */
  signIn: () => void;
  /** Sign out of Entra and come back to the current page (signOutReturn.ts). */
  signOut: () => void;
  /** An admin API bound to this session's token. Null until authorised. */
  api: AdminApi | null;
  retryIdentity: () => void;
  /**
   * True from the moment a silent token renewal has failed and a full-page
   * redirect to Entra is being started. The admin gate shows "Taking you to
   * sign-in" for it - because until the navigation actually happens, the only
   * other thing on screen would be "Checking your access", which is a lie the
   * user cannot act on. See the note in acquireToken.
   */
  redirectingToSignIn: boolean;
  /**
   * Asks for the MSAL bootstrap to be fetched and sign-in initialised. The admin
   * layout calls it on mount; public pages never do, so an anonymous visitor's
   * browser is never sent the Entra identifiers at all. Idempotent.
   */
  ensureSignInConfig: () => void;
  /**
   * Renews an expired sign-in through Entra and back to the current page, as a
   * public page load does (acquireTokenOnPublicPage). The public shell calls it
   * when the reader moves to another page inside the site (useRenewOnNavigation,
   * 2026-10-15). Nothing happens in an anonymous tab, before MSAL is ready, or
   * while the sign-in is fresh.
   */
  renewExpiredSignIn: () => void;
  /**
   * Who the public pages read as (S46): see PublicReader. Anonymous - a null
   * token - in a tab with no sign-in, and while MSAL has not started.
   */
  reader: PublicReader;
}

const AuthContext = createContext<AuthState | null>(null);

function buildMsalConfig(signIn: SignInConfig): Configuration {
  return {
    auth: {
      clientId: signIn.clientId,
      authority: signIn.authority,
      // The SPA redirect URI must be registered in Entra ID exactly as this
      // resolves - see docs/ENTRA-ID-SETUP.md.
      redirectUri: `${window.location.origin}/admin`,
      // Entra returns a browser after sign-out only to a registered address, and
      // /admin is the one registered; signOut notes the page to go on to from
      // there (signOutReturn.ts). Until 2026-10-09 this was `/`, which is not.
      postLogoutRedirectUri: `${window.location.origin}/admin`,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: () => undefined,
        piiLoggingEnabled: false,
      },
    },
  };
}

/*
 * Removes MSAL's "interaction in progress" markers from sessionStorage.
 *
 * The marker is how MSAL stops two redirects racing - but the browser restores
 * sessionStorage when it restores yesterday's tabs, and a marker restored from a
 * navigation that never completed poisons every future attempt:
 * acquireTokenRedirect and loginRedirect both throw interaction_in_progress
 * forever, on a page where no interaction is happening. Clearing it is the
 * documented community workaround; the keys are matched by shape rather than
 * exact name so an MSAL upgrade that moves the key degrades to a no-op.
 */
function clearStaleMsalInteraction(): void {
  const stale: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('msal.') && key.includes('interaction')) stale.push(key);
  }
  for (const key of stale) sessionStorage.removeItem(key);
}

/*
 * "This tab has started a sign-in, or holds one" - so start MSAL on load even off
 * /admin. sessionStorage, like MSAL's own cache, so it lives exactly as long as the
 * tokens do. Storage that throws simply leaves public pages anonymous.
 */
export const SIGN_IN_HINT_KEY = 'kb.signIn';

function readSignInHint(): boolean {
  try {
    return sessionStorage.getItem(SIGN_IN_HINT_KEY) !== null;
  } catch {
    return false;
  }
}

function writeSignInHint(present: boolean): void {
  try {
    if (present) sessionStorage.setItem(SIGN_IN_HINT_KEY, '1');
    else sessionStorage.removeItem(SIGN_IN_HINT_KEY);
  } catch {
    // No storage: the sign-in still works; a public page just will not know of it.
  }
}

/*
 * When this tab last left a public page to renew an expired sign-in
 * (2026-10-15). A renewal that comes back still expired must not send the page
 * round again, and again: within RENEWAL_BACKOFF_MS the page goes without
 * "Manage" instead. Cleared when an access check succeeds.
 */
export const RENEWAL_KEY = 'kb.renewal';
const RENEWAL_BACKOFF_MS = 5 * 60_000;

function renewedRecently(): boolean {
  try {
    const startedAt = Number(sessionStorage.getItem(RENEWAL_KEY));
    return startedAt > 0 && Date.now() - startedAt < RENEWAL_BACKOFF_MS;
  } catch {
    // No storage, no guard against a loop - so no renewal.
    return true;
  }
}

function writeRenewal(startedAt: number | null): void {
  try {
    if (startedAt === null) sessionStorage.removeItem(RENEWAL_KEY);
    else sessionStorage.setItem(RENEWAL_KEY, String(startedAt));
  } catch {
    // No storage: renewedRecently already refuses.
  }
}

/*
 * "The user has to be shown something" arrives under several types, not one:
 * InteractionRequiredAuthError is the designed signal, but an expired or absent
 * refresh token can also surface as a BrowserAuthError (monitor_window_timeout -
 * the iframe route, kept here as belt-and-braces although CacheLookupPolicy
 * AccessTokenAndRefreshToken no longer takes it) or a ClientAuthError
 * (no_tokens_found - a restored session whose cache has the account but no
 * tokens). All of them have the same correct answer: the redirect.
 */
function needsInteraction(error: unknown): boolean {
  return (
    error instanceof InteractionRequiredAuthError ||
    (error instanceof BrowserAuthError && error.errorCode === 'monitor_window_timeout') ||
    (error instanceof ClientAuthError && error.errorCode === 'no_tokens_found')
  );
}

const onAdminPath = (): boolean => /^\/admin(\/|$)/.test(window.location.pathname);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  /*
   * The MSAL bootstrap is fetched separately from the general config, and only
   * once something admin-shaped happens: the page loaded on /admin (which is
   * also where the sign-in redirect returns, fragment and all), or the admin
   * layout mounted after client-side navigation. Public visitors never trip
   * either, so their browsers are never sent the Entra identifiers.
   */
  const [signInConfig, setSignInConfig] = useState<SignInConfig | null>(null);
  // Read once: whether this tab arrived holding (or finishing) a sign-in.
  const [hadSignInHint] = useState<boolean>(readSignInHint);
  const [signInWanted, setSignInWanted] = useState<boolean>(() => onAdminPath() || hadSignInHint);
  // The admin area was entered - where the access check may redirect to Entra.
  const [adminEntered, setAdminEntered] = useState<boolean>(onAdminPath);
  // A sign-in asked for before MSAL was ready; the URL to come back to.
  const [queuedSignIn, setQueuedSignIn] = useState<string | null>(null);
  const [instance, setInstance] = useState<PublicClientApplication | null>(null);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [ready, setReady] = useState(false);
  const [identity, setIdentity] = useState<AdminIdentity | null>(null);
  const [authorisationError, setAuthorisationError] = useState<string | null>(null);
  const [checkingIdentity, setCheckingIdentity] = useState(false);
  const [identityAttempt, setIdentityAttempt] = useState(0);
  const [redirectingToSignIn, setRedirectingToSignIn] = useState(false);

  // 1. Runtime configuration.
  useEffect(() => {
    let cancelled = false;

    getClientConfig()
      .then((loaded) => {
        if (!cancelled) setConfig(loaded);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setConfigError(
          error instanceof Error
            ? `The server configuration could not be loaded: ${error.message}`
            : 'The server configuration could not be loaded.',
        );
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // 2. The MSAL bootstrap, once the admin area asks for it.
  useEffect(() => {
    if (!config || !signInWanted || signInConfig) return;

    // Without Entra ID settings there is nothing to fetch. The app still
    // renders; the admin gate explains what is missing.
    if (!config.auth.configured) {
      writeSignInHint(false);
      setReady(true);
      return;
    }

    let cancelled = false;
    getSignInConfig()
      .then((loaded) => {
        if (!cancelled) setSignInConfig(loaded);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // No sign-in to finish or restore: later pages need not try again.
        writeSignInHint(false);
        setConfigError(
          error instanceof Error
            ? `Sign-in configuration could not be loaded: ${error.message}`
            : 'Sign-in configuration could not be loaded.',
        );
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [config, signInWanted, signInConfig]);

  // 3. MSAL, once there is something to configure it with.
  useEffect(() => {
    if (!signInConfig) return;

    let cancelled = false;

    const created = new PublicClientApplication(buildMsalConfig(signInConfig));

    void (async () => {
      try {
        // MSAL v3 requires explicit initialisation before any other call.
        await created.initialize();

        // Consume the redirect fragment before React Router reads the URL.
        const result = await created.handleRedirectPromise();
        if (cancelled) return;

        // No fragment was consumed, so no interaction is genuinely in progress -
        // anything claiming otherwise is yesterday's marker, restored with the
        // session, and it would block every sign-in attempt from here on.
        if (!result) clearStaleMsalInteraction();

        const active = result?.account ?? created.getActiveAccount() ?? created.getAllAccounts()[0] ?? null;
        if (active) created.setActiveAccount(active);
        // Held: keep starting MSAL on public pages. None: this tab is anonymous again.
        writeSignInHint(active !== null);

        created.addEventCallback((event) => {
          if (
            event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
          ) {
            const payload = event.payload as AuthenticationResult | null;
            const nextAccount = payload?.account ?? null;
            if (nextAccount) {
              created.setActiveAccount(nextAccount);

              // Store it only when it is genuinely a different account.
              //
              // ACQUIRE_TOKEN_SUCCESS fires on every silent token acquisition -
              // which is every admin API call - and carries a newly constructed
              // AccountInfo each time. Setting state unconditionally would change
              // `account` on each call, rebuild the admin api, and restart the
              // effect that made the call: a loop with no exit, which pins the UI
              // on "Checking your access" and hides what /me actually answered.
              setAccount((current) =>
                current?.homeAccountId === nextAccount.homeAccountId ? current : nextAccount,
              );
            }
          }
          if (event.eventType === EventType.LOGOUT_SUCCESS) {
            writeSignInHint(false);
            setAccount(null);
            setIdentity(null);
          }
        });

        setInstance(created);
        setAccount(active);
      } catch (error) {
        if (cancelled) return;
        // A failed or abandoned sign-in must not make every later page start MSAL.
        writeSignInHint(false);
        setConfigError(
          error instanceof Error
            ? `Sign-in could not be initialised: ${error.message}`
            : 'Sign-in could not be initialised.',
        );
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signInConfig]);

  /*
   * The request for a sign-in that comes back to `startPage`. The start page is
   * read when the reader clicks, not when the configuration arrived: until
   * 2026-10-09 it was memoised on the config, so a sign-in begun after a
   * client-side navigation returned to wherever the config had first loaded.
   */
  const loginRequestFor = useCallback(
    (startPage: string) => ({
      scopes: signInConfig ? [signInConfig.apiScope] : [],
      redirectStartPage: startPage,
    }),
    [signInConfig],
  );

  /**
   * Silent first, then a redirect. `acquireTokenSilent` covers the common case
   * from cache or a refresh token; InteractionRequiredAuthError is the signal that
   * the user genuinely has to be shown something - consent, MFA, a re-login.
   */
  const acquireToken = useCallback(async (): Promise<string> => {
    if (!instance || !signInConfig) throw new Error('Sign-in is not available.');

    const active = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!active) throw new Error('You are not signed in.');

    try {
      const result = await instance.acquireTokenSilent({
        scopes: [signInConfig.apiScope],
        account: active,
        /*
         * Cache and refresh token ONLY - never the hidden-iframe fallback.
         *
         * Without this, a silent call whose refresh token has aged out makes one
         * last attempt: a concealed /authorize?prompt=none iframe. In any browser
         * that partitions third-party cookies that iframe stalls for ~10 seconds
         * and dies as BrowserAuthError monitor_window_timeout - measured on the
         * deployed test slot with a genuinely expired session, 2026-08-28. The
         * full-page redirect below achieves the same silent SSO (Entra's cookie
         * is first-party there) without an iframe, so the iframe buys nothing
         * here except a ten-second stall in front of the same outcome.
         */
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return result.accessToken;
    } catch (error) {
      // needsInteraction lists the shapes "show the user something" arrives in.
      if (needsInteraction(error)) {
        /*
         * The session genuinely needs the user again - typically the next day,
         * when a SPA refresh token has aged out (see needsInteraction above for
         * the shapes that arrives in). The redirect usually round-trips
         * through Entra without showing anything (the Entra cookie outlives our
         * token), which is the "properly refreshing the sign-in" a returning
         * administrator expects.
         *
         * Two things this block guarantees that a bare acquireTokenRedirect does
         * not: the UI says what is happening (redirectingToSignIn - the promise
         * below never resolves, so without it the screen would sit on "Checking
         * your access" until navigation wins the race), and a redirect that
         * CANNOT start - a stale interaction marker, anything - degrades to the
         * sign-in gate rather than to a dead spinner.
         */
        setRedirectingToSignIn(true);
        try {
          await instance.acquireTokenRedirect({
            scopes: [signInConfig.apiScope],
            account: active,
          });
        } catch (redirectError) {
          if (
            redirectError instanceof BrowserAuthError &&
            redirectError.errorCode === 'interaction_in_progress'
          ) {
            clearStaleMsalInteraction();
            await instance.acquireTokenRedirect({
              scopes: [signInConfig.apiScope],
              account: active,
            });
          } else {
            // The redirect could not start at all. Fall back to the one thing
            // that always works: forget the local session and offer the button.
            setRedirectingToSignIn(false);
            instance.setActiveAccount(null);
            writeSignInHint(false);
            setAccount(null);
            setIdentity(null);
            throw redirectError;
          }
        }
        // Navigation is under way; this rejection is never observed.
        throw new Error('Redirecting to sign in...');
      }
      throw error;
    }
  }, [instance, signInConfig]);

  /*
   * Keyed on the account *id*, never the account object.
   *
   * createAdminApi does not use the account - it only needs to know that there is
   * one, and which. Depending on the object would rebuild the api whenever MSAL
   * handed back a new AccountInfo, and everything downstream of it with it. The id
   * is a string, so it is stable across token acquisitions and changes only when
   * the signed-in account really changes.
   */
  const accountId = account?.homeAccountId ?? null;

  const api = useMemo(
    () => (instance && accountId ? createAdminApi(acquireToken) : null),
    [instance, accountId, acquireToken],
  );

  /*
   * The access check's token on a public page: from the cache or a refresh token
   * when it can be had; when the sign-in has expired, the page goes through Entra
   * and comes back to itself.
   *
   * Until 2026-10-15 this was cache and refresh token ONLY, so a reader was never
   * sent to Entra for a link they did not ask for. But a SPA's refresh token lives
   * 24 hours, fixed, so the next day the check got no token: the header kept "Sign
   * out" (MSAL still holds the account) and lost "Manage", even after Ctrl+F5 - the
   * owner's report from TEST, 2026-10-15. Renewing on page load was the owner's
   * choice over remembering the last verdict; the hidden iframe stays out (it
   * stalls and fails, acquireToken says why). The renewal is the admin area's own,
   * with three differences:
   *   - it comes back to THIS page (redirectStartPage) - by way of /admin, as
   *     "Sign in" does (loginRedirect is acquireTokenRedirect in MSAL 3);
   *   - once per RENEWAL_BACKOFF_MS: one that came back still expired leaves
   *     "Manage" out instead of sending the page round again;
   *   - one that cannot start forgets nothing - the reader is reading, and the
   *     header just goes without "Manage", as it used to.
   * The check runs when a page loads; moving to another page inside the site
   * renews through renewExpiredSignIn, below. A signed-in reader who is not an
   * administrator is renewed too: the server's 403 comes after the token.
   */
  const acquireTokenOnPublicPage = useCallback(async (): Promise<string> => {
    if (!instance || !signInConfig) throw new Error('Sign-in is not available.');
    const active = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!active) throw new Error('You are not signed in.');
    try {
      const result = await instance.acquireTokenSilent({
        scopes: [signInConfig.apiScope],
        account: active,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      return result.accessToken;
    } catch (error) {
      if (!needsInteraction(error) || renewedRecently()) throw error;
      writeRenewal(Date.now());
      await instance.acquireTokenRedirect({
        scopes: [signInConfig.apiScope],
        account: active,
        redirectStartPage: window.location.href,
      });
      // Navigation is under way; this rejection is never observed.
      throw new Error('Renewing the sign-in...');
    }
  }, [instance, signInConfig]);

  const publicPageApi = useMemo(
    () => (instance && accountId ? createAdminApi(acquireTokenOnPublicPage) : null),
    [instance, accountId, acquireTokenOnPublicPage],
  );

  /*
   * The same renewal when the reader moves to another page inside the site
   * (2026-10-15, the owner's request). The access check runs only when a page
   * loads, so without this a tab open across the expiry kept its page load's
   * verdict and met the expiry only at its next admin call. A token from the
   * cache costs nothing; an expired access token costs one refresh-token request
   * to Entra, about once an hour; only an expired sign-in leaves the page. No
   * access check: the verdict stands until the page is loaded again.
   */
  const renewExpiredSignIn = useCallback(() => {
    if (!instance || !accountId) return;
    acquireTokenOnPublicPage().catch(() => undefined);
  }, [instance, accountId, acquireTokenOnPublicPage]);

  // The account (and retry) a check last confirmed as an administrator.
  const confirmedAdmin = useRef<{ accountId: string; attempt: number } | null>(null);

  // 3. Ask the server whether this account may administer the knowledge base.
  useEffect(() => {
    // In the admin area the interactive api; on a public page the one that renews
    // an expired sign-in and comes back to the page (acquireTokenOnPublicPage).
    const checker = adminEntered ? api : publicPageApi;
    if (!checker || !accountId) {
      confirmedAdmin.current = null;
      setIdentity(null);
      setAuthorisationError(null);
      return;
    }

    // Entering the admin area after a public page already confirmed this account:
    // nothing new to ask, and asking would put "Checking your access" over the
    // admin pages for a moment. A retry (identityAttempt) always asks again.
    if (
      adminEntered &&
      confirmedAdmin.current?.accountId === accountId &&
      confirmedAdmin.current.attempt === identityAttempt
    ) {
      return;
    }

    let cancelled = false;
    setCheckingIdentity(true);
    setAuthorisationError(null);

    /*
     * Timeboxed, because this screen has hung before (see HANDOFF section 6.2)
     * and the redirect path above deliberately leaves a promise pending while
     * the browser navigates. If neither an answer nor a navigation has happened
     * in 20 seconds, concluding with a retryable error beats a spinner that
     * nothing will ever resolve.
     */
    const timeout = new Promise<never>((_, reject) => {
      setTimeout(
        () =>
          reject(
            new Error(
              'The access check did not complete within 20 seconds. Retry, or sign out and back in.',
            ),
          ),
        20_000,
      );
    });

    const check = checker.me();
    // The race may settle on the timeout first; without this, a me() rejection
    // arriving later is an unhandled-rejection console error.
    check.catch(() => undefined);

    Promise.race([check, timeout])
      .then((result) => {
        if (cancelled) return;
        // The sign-in works again: a later expiry may renew it once more.
        writeRenewal(null);
        confirmedAdmin.current = result.isAdmin ? { accountId, attempt: identityAttempt } : null;
        setIdentity(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        confirmedAdmin.current = null;
        setIdentity(null);
        // A public page shows no verdict; the admin gate is where one is read.
        if (!adminEntered) {
          setAuthorisationError(null);
          return;
        }
        setAuthorisationError(
          error instanceof ApiError || error instanceof Error
            ? error.message
            : 'Your access could not be confirmed. Try signing in again.',
        );
      })
      .finally(() => {
        if (!cancelled) setCheckingIdentity(false);
      });

    return () => {
      cancelled = true;
    };
  }, [api, publicPageApi, accountId, adminEntered, identityAttempt]);

  const signIn = useCallback(() => {
    const startPage = window.location.href;
    // The page MSAL navigates back to must start MSAL to finish the sign-in.
    writeSignInHint(true);
    if (instance) {
      void instance.loginRedirect(loginRequestFor(startPage));
      return;
    }
    // A public page: fetch the sign-in configuration and start MSAL first.
    setSignInWanted(true);
    setQueuedSignIn(startPage);
  }, [instance, loginRequestFor]);

  /*
   * The queued sign-in, as soon as MSAL is ready for it.
   *
   * The marker is written AGAIN here, not only in signIn. Starting MSAL is what
   * made it ready, and its start-up found no account and cleared the marker -
   * "this tab is anonymous" - a moment before this redirect. Without the rewrite
   * the page MSAL brings the reader back to has no marker, never starts MSAL,
   * and the sign-in response waits unprocessed until the next click: the owner
   * found exactly that on TEST, 2026-10-09, signing in twice to see it once.
   */
  useEffect(() => {
    if (!queuedSignIn || !instance) return;
    setQueuedSignIn(null);
    writeSignInHint(true);
    void instance.loginRedirect(loginRequestFor(queuedSignIn));
  }, [queuedSignIn, instance, loginRequestFor]);

  /*
   * Sign out of Entra and come back to this page: by way of /admin, the address
   * Entra will return to, where main.tsx follows the note on before rendering.
   */
  const signOut = useCallback(() => {
    if (!instance) return;
    writeSignInHint(false);
    noteSignOutReturn(window.location.href);
    const active = instance.getActiveAccount();
    void instance.logoutRedirect(active ? { account: active } : {});
  }, [instance]);

  /*
   * The public pages' reader. Not ready while this tab holds a sign-in MSAL has
   * not restored yet - the same condition as `restoringSession` - so a page
   * waits rather than reading anonymously and then again. Keyed on the account
   * id, so a page reloads its content when the reader really changes, never on
   * a token renewal.
   */
  const restoringSession = hadSignInHint && !ready;
  const reader = useMemo<PublicReader>(
    () => ({
      ready: !restoringSession,
      key: accountId ?? 'anonymous',
      token: instance && accountId ? acquireTokenOnPublicPage : null,
    }),
    [restoringSession, accountId, instance, acquireTokenOnPublicPage],
  );

  const value = useMemo<AuthState>(
    () => ({
      config,
      configError,
      ready,
      signedIn: Boolean(account),
      restoringSession,
      account,
      identity,
      authorisationError,
      checkingIdentity,
      signIn,
      signOut,
      api: identity?.isAdmin ? api : null,
      retryIdentity: () => setIdentityAttempt((attempt) => attempt + 1),
      ensureSignInConfig: () => {
        setSignInWanted(true);
        setAdminEntered(true);
      },
      redirectingToSignIn,
      renewExpiredSignIn,
      reader,
    }),
    [
      config,
      configError,
      ready,
      restoringSession,
      account,
      identity,
      authorisationError,
      checkingIdentity,
      signIn,
      signOut,
      api,
      renewExpiredSignIn,
      reader,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider.');
  return context;
}

/** useAuth for content that also renders outside the provider: null there. */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

/**
 * The runtime configuration this provider loaded from /api/config, for the
 * site chrome rather than for sign-in - today the footer, which asks it which
 * footer this deployment shows. Null until it has loaded, and null outside the
 * provider rather than an error: the chrome renders in places that never sign
 * anyone in, and "no configuration yet" is a state it already has to handle.
 */
export function useClientConfig(): ClientConfig | null {
  return useContext(AuthContext)?.config ?? null;
}

/**
 * Who the public pages read as (S46). The anonymous reader outside the provider -
 * the chrome and the tests render pages without one, and anonymous is what a
 * page read as before private articles.
 */
export function usePublicReader(): PublicReader {
  return useContext(AuthContext)?.reader ?? ANONYMOUS_READER;
}

/**
 * The admin API, for components that render only inside the admin gate and can
 * therefore rely on it existing.
 */
export function useAdminApi(): AdminApi {
  const { api } = useAuth();
  if (!api) throw new Error('The admin API is only available to a signed-in administrator.');
  return api;
}
